import { Check, Globe, Users } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export type CommentVisibility = "public" | "internal";

const OPTIONS: {
  key: CommentVisibility;
  label: string;
  description: string;
  Icon: typeof Globe;
}[] = [
  {
    key: "public",
    label: "Public Comment",
    description: "All members and Share viewers can see",
    Icon: Globe,
  },
  {
    key: "internal",
    label: "Internal Comment",
    description: "Only members with access can see",
    Icon: Users,
  },
];

type Props = {
  value: CommentVisibility;
  onChange: (next: CommentVisibility) => void;
  /** Compact = icon-only trigger; expanded = icon + label pill. */
  compact?: boolean;
};

/**
 * Public / Internal (team-only) visibility picker (2026-07-11).
 *
 * Public = default; visible to everyone with access to the video (workspace
 * members + all guests on a share link).
 * Internal = workspace-only; guests reading via a share token don't see the
 * comment. Server-side filter enforces this in GET /watch/:token/comments.
 *
 * Anyone can set either value — including guests. A guest's internal comment
 * is visible to the workspace but hidden from other guests.
 */
export function VisibilityDropdown({ value, onChange, compact }: Props) {
  const current = OPTIONS.find((o) => o.key === value) ?? OPTIONS[0];
  const CurrentIcon = current.Icon;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={current.label}
        title={current.label}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-md border border-border bg-background text-xs font-medium text-foreground transition-colors hover:bg-secondary/40",
          compact ? "h-7 w-7 justify-center" : "h-7 px-2",
        )}
      >
        <CurrentIcon className="h-3.5 w-3.5" />
        {!compact && (
          <>
            <span>{value === "internal" ? "Internal" : "Public"}</span>
            <svg viewBox="0 0 12 12" className="h-3 w-3 shrink-0 text-muted-foreground">
              <path d="M3 5l3 3 3-3" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 p-1">
        {OPTIONS.map(({ key, label, description, Icon }) => {
          const active = value === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => onChange(key)}
              className={cn(
                "flex w-full items-start gap-2 rounded-md px-2 py-2 text-left transition-colors",
                active ? "bg-secondary/60" : "hover:bg-secondary/40",
              )}
            >
              <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-foreground">{label}</div>
                <div className="text-[11px] text-muted-foreground">{description}</div>
              </div>
              {active && <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />}
            </button>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
